import React from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import { useParams, useHistory, useLocation } from 'react-router-dom'
import {
  Button,
  CircularProgress,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Tabs,
  Tab
} from '@material-ui/core'
import {
  ArrowBack,
  GetApp,
  FileCopy
} from '@material-ui/icons'
import OutputTable from './OutputTable'
import ServiceOutputTable from './ServiceOutputTable'
import BidResponseOutputTable from './BidResponseOutputTable'
import BidEstimationHeader from '../shared/BidEstimationHeader'

const BidOutputView = (props) => {
  const { projectId } = useParams()
  const history = useHistory()
  const location = useLocation()
  const [project, setProject] = React.useState(null)
  const [selectedBuilding, setSelectedBuilding] = React.useState('')
  const [outputData, setOutputData] = React.useState(null)
  const [loading, setLoading] = React.useState(false)
  const [projectLoading, setProjectLoading] = React.useState(false)
  const [copyNotice, setCopyNotice] = React.useState(false)
  const initialTab = React.useMemo(() => {
    const queryTab = new URLSearchParams(location.search).get('tab')
    return queryTab === 'bid-response' ? 2 : queryTab === 'service' ? 1 : 0
  }, [location.search])
  const [activeTab, setActiveTab] = React.useState(initialTab)
  const [serviceOutputData, setServiceOutputData] = React.useState(null)
  const [serviceOutputLoading, setServiceOutputLoading] = React.useState(false)
  const [bidRespOutputData, setBidRespOutputData] = React.useState(null)
  const [bidRespOutputLoading, setBidRespOutputLoading] = React.useState(false)

  React.useEffect(() => {
    setProject(null)
    setSelectedBuilding('')
    setOutputData(null)
    setServiceOutputData(null)
    setBidRespOutputData(null)
    fetchProject()
  }, [projectId])

  React.useEffect(() => {
    setActiveTab(initialTab)
  }, [initialTab])

  React.useEffect(() => {
    if (selectedBuilding) {
      fetchOutput(selectedBuilding)
    } else {
      setOutputData(null)
    }
  }, [selectedBuilding, projectId])

  React.useEffect(() => {
    if (activeTab === 1 && !serviceOutputData && !serviceOutputLoading) fetchServiceOutput()
    if (activeTab === 2 && !bidRespOutputData && !bidRespOutputLoading) fetchBidResponseOutput()
  }, [activeTab, projectId, serviceOutputData, serviceOutputLoading, bidRespOutputData, bidRespOutputLoading])

  const fetchProject = () => {
    setProjectLoading(true)
    axios.get(`${props.url}/api/bid-estimation/projects/${projectId}`).then(res => {
      setProject(res.data)
      setProjectLoading(false)
      // Auto-select first building if available
      const buildings = res.data?.buildings || []
      const buildingNames = buildings.map(building => building.name || building)
      if (buildingNames.length > 0 && (!selectedBuilding || !buildingNames.includes(selectedBuilding))) {
        setSelectedBuilding(buildingNames[0])
      }
    }).catch(err => {
      console.error(err)
      setProjectLoading(false)
    })
  }

  const fetchOutput = (building) => {
    setLoading(true)
    axios.get(`${props.url}/api/bid-estimation/projects/${projectId}/output/${encodeURIComponent(building)}`).then(res => {
      setOutputData(res.data)
      setLoading(false)
    }).catch(err => {
      console.error(err)
      setLoading(false)
    })
  }

  const handleExport = () => {
    window.open(
      `${props.url}/api/bid-estimation/projects/${projectId}/export/${encodeURIComponent(selectedBuilding)}`,
      '_blank'
    )
  }

  const fetchServiceOutput = () => {
    setServiceOutputLoading(true)
    axios.get(`${props.url}/api/bid-estimation/projects/${projectId}/service-output`).then(res => {
      setServiceOutputData(res.data)
      setServiceOutputLoading(false)
    }).catch(err => {
      console.error(err)
      setServiceOutputLoading(false)
    })
  }

  const handleExportServiceOutput = () => {
    window.open(
      `${props.url}/api/bid-estimation/projects/${projectId}/service-output/export`,
      '_blank'
    )
  }

  const fetchBidResponseOutput = () => {
    setBidRespOutputLoading(true)
    axios.get(`${props.url}/api/bid-estimation/projects/${projectId}/bid-response-output`).then(res => {
      setBidRespOutputData(res.data)
      setBidRespOutputLoading(false)
    }).catch(err => {
      console.error(err)
      setBidRespOutputLoading(false)
    })
  }

  const handleExportBidResponseOutput = () => {
    window.open(
      `${props.url}/api/bid-estimation/projects/${projectId}/bid-response-output/export`,
      '_blank'
    )
  }

  const handleCopyValues = () => {
    if (!outputData || !outputData.items) return
    const lines = outputData.items.map(item => {
      const unitCost = item.unitCost || ''
      const totalCost = item.totalCost || ''
      return `${unitCost}\t${totalCost}`
    })
    const text = lines.join('\n')
    navigator.clipboard.writeText(text).then(() => {
      setCopyNotice(true)
    }).catch(err => {
      console.error('Copy failed:', err)
    })
  }

  const buildings = project?.buildings || []
  const buildingNames = buildings.map(b => b.name || b)

  if (projectLoading) {
    return (
      <div style={{ padding: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
          <CircularProgress />
        </div>
      </div>
    )
  }

  return (
    <div style={{ padding: 20 }}>
      <BidEstimationHeader
        backTo={`/bid-estimation/${projectId}`}
        backLabel="Project"
        breadcrumbs={[
          { label: 'Bid Estimation', to: '/bid-estimation' },
          { label: project?.name || 'Project', to: `/bid-estimation/${projectId}` },
          { label: 'Output' }
        ]}
        title={`Output${project?.name ? ' · ' + project.name : ''}`}
      />

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(e, v) => {
          setActiveTab(v)
          if (v === 1 && !serviceOutputData) fetchServiceOutput()
          if (v === 2 && !bidRespOutputData) fetchBidResponseOutput()
        }}
        indicatorColor="primary"
        textColor="primary"
        style={{ marginBottom: 16 }}
      >
        <Tab label="Owner Bid Schedule" />
        <Tab label="Internal Service Estimate" />
        <Tab label="Priced Bid Response Export" />
      </Tabs>

      {activeTab === 0 && (
        <>
          {/* Bid Response Controls */}
          <Grid container spacing={2} alignItems="center" style={{ marginBottom: 20 }}>
            <Grid item>
              <FormControl variant="outlined" size="small" style={{ minWidth: 220 }}>
                <InputLabel>Building</InputLabel>
                <Select
                  value={selectedBuilding}
                  onChange={e => setSelectedBuilding(e.target.value)}
                  label="Building"
                >
                  {buildingNames.map(name => (
                    <MenuItem key={name} value={name}>{name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                startIcon={<GetApp />}
                onClick={handleExport}
                disabled={!selectedBuilding || !outputData}
              >
                Export to Excel
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="outlined"
                startIcon={<FileCopy />}
                onClick={handleCopyValues}
                disabled={!outputData || !outputData.items}
              >
                Copy Values
              </Button>
            </Grid>
          </Grid>

          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
              <CircularProgress />
            </div>
          ) : (
            <OutputTable data={outputData} />
          )}
        </>
      )}

      {activeTab === 1 && (
        <>
          <Grid container spacing={2} alignItems="center" style={{ marginBottom: 20 }}>
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                startIcon={<GetApp />}
                onClick={handleExportServiceOutput}
                disabled={!serviceOutputData}
              >
                Export Service Estimate
              </Button>
            </Grid>
          </Grid>

          {serviceOutputLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
              <CircularProgress />
            </div>
          ) : (
            <ServiceOutputTable data={serviceOutputData} />
          )}
        </>
      )}

      {activeTab === 2 && (
        <>
          <Grid container spacing={2} alignItems="center" style={{ marginBottom: 20 }}>
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                startIcon={<GetApp />}
                onClick={handleExportBidResponseOutput}
                disabled={!bidRespOutputData}
              >
                Export Priced Bid Response
              </Button>
            </Grid>
          </Grid>

          {bidRespOutputLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
              <CircularProgress />
            </div>
          ) : (
            <BidResponseOutputTable data={bidRespOutputData} />
          )}
        </>
      )}

      <Snackbar
        open={copyNotice}
        autoHideDuration={2000}
        onClose={() => setCopyNotice(false)}
        message="Unit cost and total cost values copied to clipboard"
      />
    </div>
  )
}

const mapStateToProps = (state) => ({
  url: state.url,
  user: state.user,
  authenticated: state.authenticated
})

export default connect(mapStateToProps)(BidOutputView)
